import QuickLRU from 'quick-lru';

import type { TreeData } from '../../../../shared/components/tree/useTreeApi';
import { SKILL_TAXONOMY_SELECTION_LIST_KEYS } from '../../../../shared/selection-lists/types';
import type { CustomSkillTreeNode } from './skillTaxonomyTree';
import { toNestedTaxonomyTree } from './taxonomyNestedTree';

type NestedSkillReferenceCacheEntry = {
  sourceRef: TreeData[];
  nested: TreeData[];
};

const nestedSkillReferenceTreeCache = new QuickLRU<
  string,
  NestedSkillReferenceCacheEntry
>({ maxSize: 8 });

const customSkillNodesByNestedRoots = new WeakMap<
  TreeData[],
  CustomSkillTreeNode[]
>();

/**
 * Skill TaxonomyTree levels for the dashboard custom-skill tree (see Bruno:
 * taxonomy skill selection). Four API levels map to `skill_0` … `skill_3`,
 * matching `SKILL_TAXONOMY_SELECTION_LIST_KEYS` and the visible CUSTOM_SKILL
 * filter definition. A fifth legacy depth (`skill_4`) is not returned by this
 * TaxonomyTree request; extend `SKILL_REFERENCE_TAXONOMY_TREE_LEVELS` when the
 * API exposes an additional level.
 */
export const SKILL_REFERENCE_TAXONOMY_TREE_LEVELS = [3, 5, 6, 7] as const;

export const buildSkillLevelIdMapFromTaxonomyLevels = (
  levels: readonly { id: number }[]
): Record<string, number> => {
  const sorted = [...levels].sort((a, b) => a.id - b.id);
  const map: Record<string, number> = {};
  const keys = SKILL_TAXONOMY_SELECTION_LIST_KEYS;
  sorted.forEach((level, index) => {
    const key = keys[index];
    if (key !== undefined) {
      map[key] = level.id;
    }
  });
  return map;
};

const nestedSkillTaxonomyTreeNodeToCustom = (
  node: TreeData,
  depth: number
): CustomSkillTreeNode => {
  const children = node.children?.length
    ? node.children.map((c) =>
        nestedSkillTaxonomyTreeNodeToCustom(c, depth + 1)
      )
    : undefined;

  return {
    id: node.id,
    name: node.name,
    depth,
    ...(node.taxonomyTitles != null && { taxonomyTitles: node.taxonomyTitles }),
    ...(node.suggestedTitles && { suggestedTitles: node.suggestedTitles }),
    ...(children && children.length > 0 ? { children } : {}),
  };
};

export const nestedSkillTaxonomyTreeToCustomSkillNodes = (
  nodes: TreeData[]
): CustomSkillTreeNode[] =>
  nodes.map((node) => nestedSkillTaxonomyTreeNodeToCustom(node, 0));

export const buildNestedSkillTaxonomyTreeForReference = (
  rawNormalizedRoots: TreeData[]
): TreeData[] => {
  if (rawNormalizedRoots.length === 0) return [];
  return toNestedTaxonomyTree(
    rawNormalizedRoots,
    SKILL_TAXONOMY_SELECTION_LIST_KEYS,
    undefined,
    0,
    true,
    false
  );
};

/**
 * TaxonomyTree already returns a nested hierarchy; this maps it once to
 * selection-list-shaped nodes (composite ids + levelId). Results are memoized
 * per `taxonomyQueryCacheKey` and urql snapshot (`sourceRef === rawRoots`) so
 * multiple `useCustomSkillTaxonomyTree` consumers do not each pay for
 * `toNestedTaxonomyTree`.
 */
export const getOrBuildNestedSkillReferenceTree = (
  taxonomyQueryCacheKey: string | null,
  rawRoots: TreeData[]
): TreeData[] | null => {
  if (rawRoots.length === 0) return null;
  if (taxonomyQueryCacheKey === null) {
    return buildNestedSkillTaxonomyTreeForReference(rawRoots);
  }
  const hit = nestedSkillReferenceTreeCache.get(taxonomyQueryCacheKey);
  if (hit !== undefined && hit.sourceRef === rawRoots) {
    return hit.nested;
  }
  const nested = buildNestedSkillTaxonomyTreeForReference(rawRoots);
  nestedSkillReferenceTreeCache.set(taxonomyQueryCacheKey, {
    sourceRef: rawRoots,
    nested,
  });
  return nested;
};

export const getCachedCustomSkillNodesFromNested = (
  nestedRoots: TreeData[]
): CustomSkillTreeNode[] => {
  const hit = customSkillNodesByNestedRoots.get(nestedRoots);
  if (hit !== undefined) return hit;
  const custom = nestedSkillTaxonomyTreeToCustomSkillNodes(nestedRoots);
  customSkillNodesByNestedRoots.set(nestedRoots, custom);
  return custom;
};

export const buildCustomSkillTreeFromSkillTaxonomyReference = (
  rawNormalizedRoots: TreeData[]
): CustomSkillTreeNode[] | null => {
  if (rawNormalizedRoots.length === 0) return null;
  const nested = buildNestedSkillTaxonomyTreeForReference(rawNormalizedRoots);
  const custom = nestedSkillTaxonomyTreeToCustomSkillNodes(nested);
  return custom.length > 0 ? custom : null;
};
